// src/pages/EditarImovel.jsx
// EditarImovel revisado: endereço livre, sem "suítes" na UI, permuta estruturada e preço numérico
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IMaskInput } from 'react-imask';
import { uploadImagem } from '../services/uploadImagem';
import { editarImovel, deleteImagemImovel } from '../services/imoveisService';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth'; // se seu projeto usa o context, troque para ../context/AuthContext
import { FaTrash } from 'react-icons/fa';
import { getDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

// opções
const tiposImovel = [
  'Casa',
  'Apartamento',
  'Apartamento na planta',
  'Kitnet',
  'Terreno / Área',
  'Sala comercial',
  'Ponto comercial',
  'Galpão / Barracão',
  'Empreendimentos',
  'Chácara',
  'Fazenda',
];
const estadosPermitidos = ['MT'];
const cidadesPermitidas = [
  'Cuiabá',
  'Várzea Grande',
  'Rondonópolis',
  'Sinop',
  'Sorriso',
];

const Card = ({ title, children, className = '' }) => (
  <div
    className={`bg-white p-6 rounded-2xl shadow-lg border border-gray-100 ${className}`}
  >
    <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
    {children}
  </div>
);

// helpers
const toNumber = (val) => {
  if (val === null || val === undefined) return null;
  const digits = String(val).replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) : null;
};
const getIniciais = (nomeCompleto) => {
  if (!nomeCompleto) return '';
  const partes = nomeCompleto.trim().split(' ').filter(Boolean);
  const first = partes[0]?.[0] || '';
  const last = partes.length > 1 ? partes[partes.length - 1]?.[0] : '';
  return `${first}${last}`.toUpperCase();
};
const yes = (v) =>
  v === true || (typeof v === 'string' && v.toLowerCase() === 'sim');

const EditarImovel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, usuario } = useAuth();

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imagensExistentes, setImagensExistentes] = useState([]);
  const [imagensNovas, setImagensNovas] = useState([]);

  // Carrega o imóvel
  useEffect(() => {
    const carregarImovel = async () => {
      try {
        const snap = await getDoc(doc(db, 'imoveis', id));
        if (!snap.exists()) throw new Error('Imóvel não encontrado.');
        const dados = snap.data();

        const refCodigo = (dados.refCodigo || dados.codigoReferencia || '')
          .toString()
          .toUpperCase();

        setForm({
          ...dados,
          // normalizações de UI
          refCodigo,
          preco: String(dados.preco ?? ''),
          financiamento: !!dados.financiamento,
          condominio: !!dados.condominio,
          piscina: !!dados.piscina,
          churrasqueira: !!dados.churrasqueira,
          endereco: dados.rua || dados.endereco || '',

          // 🔢 novos campos alinhados com o cadastro
          statusNegocio: dados.statusNegocio || 'ativo',
          temPlaca: !!dados.temPlaca,

          // permuta em formato de UI
          aceitaPermutaVeiculos: yes(dados.aceitaPermutaVeiculos)
            ? 'sim'
            : 'nao',
          aceitaPermutaImoveis: yes(dados.aceitaPermutaImoveis) ? 'sim' : 'nao',
          permutaNegociavel: yes(dados.permutaNegociavel) ? 'sim' : 'nao',
          percentualPermuta:
            (dados.percentualPermuta ?? '') === 0
              ? '' // 0 aqui significa "negociável"
              : String(dados.percentualPermuta ?? ''),
        });

        setImagensExistentes(Array.isArray(dados.imagens) ? dados.imagens : []);
      } catch (err) {
        console.error(err);
        toast.error('Erro ao carregar o imóvel');
        navigate('/perfil');
      }
    };
    carregarImovel();
  }, [id, navigate]);

  // refCodigo fallback
  useEffect(() => {
    if (!form) return;
    if (!form.refCodigo || !form.refCodigo.trim()) {
      const iniciais = getIniciais(usuario?.nome || user?.displayName || '');
      setForm((prev) => ({ ...prev, refCodigo: `${iniciais || 'XX'}001` }));
    }
  }, [form, user, usuario]);

  const handleChange = (campo, valor) =>
    setForm((prev) => ({ ...prev, [campo]: valor }));

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!form) return;

    setLoading(true);
    try {
      // upload novas imagens
      const novas = await Promise.all(
        imagensNovas.map((file) => uploadImagem(file, user))
      );
      const novasImgs = novas.map((img) => ({ url: img.url, path: img.path }));
      const todasImagens = [...imagensExistentes, ...novasImgs];

      const precoNumerico = toNumber(form.preco);

      // permuta normalizada
      const isNegociavel =
        form.percentualPermuta === 'negociavel' ||
        form.permutaNegociavel === 'sim';
      const percentualNumber = isNegociavel
        ? 0
        : parseInt(form.percentualPermuta, 10) || 0;

      const atualizado = {
        // editáveis
        refCodigo: String(form.refCodigo || '').toUpperCase(),
        tipo: form.tipo || '',
        status: form.status || '',
        // statusNegocio vai ser usado depois pra funil (ativo, vendido, perdido etc)
        statusNegocio: form.statusNegocio || 'ativo',

        publicado: true, // <- novo (booleano)
        exibirNoSite: true, // <- se o microsite usa essa flag

        // localização (sem slugs; backend cria slugs/índice)
        estado: (form.estado || '').trim(),
        cidade: (form.cidade || '').trim(),
        bairro: (form.bairro || '').trim(),

        // numéricos
        preco: precoNumerico,
        dormitorios: parseInt(form.dormitorios, 10) || 0,
        suites: 0, // suites removido da UI
        banheiros: parseInt(form.banheiros, 10) || 0,
        vagas: parseInt(form.vagas, 10) || 0,
        areaTerreno: parseInt(form.areaTerreno, 10) || 0,
        areaConstruida: parseInt(form.areaConstruida, 10) || 0,

        // endereço
        rua: form.endereco || '',
        numero: form.numero || '',
        cep: form.cep || '',

        // flags/filtros
        financiamento: !!form.financiamento,
        condominio: !!form.condominio,
        piscina: !!form.piscina,
        churrasqueira: !!form.churrasqueira,
        // 🔢 novo: imóvel com placa instalada
        temPlaca: !!form.temPlaca,

        // permuta
        aceitaPermutaVeiculos: form.aceitaPermutaVeiculos, // 'sim' | 'nao'
        aceitaPermutaImoveis: form.aceitaPermutaImoveis, // 'sim' | 'nao'
        permutaNegociavel: isNegociavel, // boolean
        percentualPermuta: percentualNumber, // number (0 se negociável)

        // imagens
        imagens: todasImagens,
        thumbnail: todasImagens[0]?.url || '',

        // auditoria
        updatedAt: serverTimestamp(),
      };

      await editarImovel(id, atualizado);
      toast.success('Imóvel atualizado com sucesso!');
      navigate('/perfil');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar as alterações');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImagemExistente = async (imagem) => {
    try {
      await deleteImagemImovel(imagem.path);
      setImagensExistentes((prev) =>
        prev.filter((img) => img.url !== imagem.url)
      );
      toast.success('Imagem removida.');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao remover imagem.');
    }
  };

  if (!form) return <p className="p-4">Carregando dados do imóvel...</p>;

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <form onSubmit={handleSalvar} className="space-y-8">
        {/* === Imagens === */}
        <Card title="Imagens do Imóvel">
          <label className="label">Imagens existentes</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {imagensExistentes.map((img) => (
              <div key={img.url} className="relative w-24 h-24 group">
                <img
                  src={img.url}
                  className="w-full h-full object-cover rounded"
                  alt="Imagem"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImagemExistente(img)}
                  className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                >
                  <FaTrash className="text-white" />
                </button>
              </div>
            ))}
          </div>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => setImagensNovas(Array.from(e.target.files || []))}
          />
        </Card>

        {/* === Básico === */}
        <Card title="Informações Básicas">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Código de Referência</label>
              <input
                type="text"
                maxLength={10}
                value={form.refCodigo || ''}
                onChange={(e) => handleChange('refCodigo', e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="label">Tipo</label>
              <select
                className="input"
                value={form.tipo || ''}
                onChange={(e) => handleChange('tipo', e.target.value)}
              >
                {tiposImovel.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Preço</label>
              <IMaskInput
                mask="R$ num"
                blocks={{
                  num: {
                    mask: Number,
                    thousandsSeparator: '.',
                    radix: ',',
                    scale: 0,
                    padFractionalZeros: false,
                    signed: false,
                  },
                }}
                unmask={true}
                value={form.preco}
                onAccept={(val) => handleChange('preco', val)}
                className="input"
              />
            </div>
          </div>
        </Card>

        {/* === Localização === */}
        <Card title="Localização">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Estado</label>
              <select
                className="input"
                value={form.estado || ''}
                onChange={(e) => handleChange('estado', e.target.value)}
              >
                {estadosPermitidos.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Cidade</label>
              <select
                className="input"
                value={form.cidade || ''}
                onChange={(e) => handleChange('cidade', e.target.value)}
              >
                {cidadesPermitidas.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Bairro</label>
              <input
                type="text"
                className="input"
                value={form.bairro || ''}
                onChange={(e) => handleChange('bairro', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Endereço</label>
              <input
                type="text"
                className="input"
                placeholder="Ex: Rua das Flores, Av. Brasil, Travessa X..."
                value={form.endereco || ''}
                onChange={(e) => handleChange('endereco', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Número</label>
              <input
                type="text"
                className="input"
                value={form.numero || ''}
                onChange={(e) => handleChange('numero', e.target.value)}
              />
            </div>
            <div>
              <label className="label">CEP</label>
              <IMaskInput
                mask="00000-000"
                value={form.cep || ''}
                onAccept={(value) => handleChange('cep', value)}
                className="input"
              />
            </div>
          </div>
        </Card>

        {/* === Características === */}
        <Card title="Características do Imóvel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['dormitorios', 'banheiros', 'vagas'].map((campo) => (
              <div key={campo}>
                <label className="label">
                  {campo.charAt(0).toUpperCase() + campo.slice(1)}
                </label>
                <select
                  className="input"
                  value={form[campo] ?? ''}
                  onChange={(e) => handleChange(campo, e.target.value)}
                >
                  <option value="">Selecionar</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                  <option value="5+">5+</option>
                </select>
              </div>
            ))}
            <div>
              <label className="label">Área do Terreno (m²)</label>
              <input
                type="number"
                className="input"
                value={form.areaTerreno ?? ''}
                onChange={(e) => handleChange('areaTerreno', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Área Construída (m²)</label>
              <input
                type="number"
                className="input"
                value={form.areaConstruida ?? ''}
                onChange={(e) => handleChange('areaConstruida', e.target.value)}
              />
            </div>
          </div>

          {/* Flags de filtro */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.piscina}
                onChange={(e) => handleChange('piscina', e.target.checked)}
              />
              <span className="text-sm text-gray-700">Piscina</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.churrasqueira}
                onChange={(e) =>
                  handleChange('churrasqueira', e.target.checked)
                }
              />
              <span className="text-sm text-gray-700">Churrasqueira</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.financiamento}
                onChange={(e) =>
                  handleChange('financiamento', e.target.checked)
                }
              />
              <span className="text-sm text-gray-700">
                Aceita financiamento
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.condominio}
                onChange={(e) => handleChange('condominio', e.target.checked)}
              />
              <span className="text-sm text-gray-700">Em condomínio</span>
            </label>
          </div>

          {/* 🔢 novo checkbox de placa */}
          <div className="mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.temPlaca}
                onChange={(e) => handleChange('temPlaca', e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                Imóvel com placa instalada
              </span>
            </label>
          </div>
        </Card>

        {/* === Permuta === */}
        <Card title="Permuta">
          <div className="space-y-4">
            {/* veículos */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-full md:w-1/2">
                Aceita veículo como parte da negociação?
              </label>
              <div className="flex gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="aceitaPermutaVeiculos"
                    value="sim"
                    checked={form.aceitaPermutaVeiculos === 'sim'}
                    onChange={(e) =>
                      handleChange('aceitaPermutaVeiculos', e.target.value)
                    }
                  />
                  <span>Sim</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="aceitaPermutaVeiculos"
                    value="nao"
                    checked={form.aceitaPermutaVeiculos === 'nao'}
                    onChange={(e) =>
                      handleChange('aceitaPermutaVeiculos', e.target.value)
                    }
                  />
                  <span>Não</span>
                </label>
              </div>
            </div>

            {/* imóveis/terreno */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-full md:w-1/2">
                Aceita terreno ou outro imóvel como parte da negociação?
              </label>
              <div className="flex gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="aceitaPermutaImoveis"
                    value="sim"
                    checked={form.aceitaPermutaImoveis === 'sim'}
                    onChange={(e) =>
                      handleChange('aceitaPermutaImoveis', e.target.value)
                    }
                  />
                  <span>Sim</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="aceitaPermutaImoveis"
                    value="nao"
                    checked={form.aceitaPermutaImoveis === 'nao'}
                    onChange={(e) =>
                      handleChange('aceitaPermutaImoveis', e.target.value)
                    }
                  />
                  <span>Não</span>
                </label>
              </div>
            </div>

            {(form.aceitaPermutaVeiculos === 'sim' ||
              form.aceitaPermutaImoveis === 'sim') && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    Qual percentual de permuta aceita?
                  </label>
                  <select
                    value={form.percentualPermuta}
                    onChange={(e) =>
                      handleChange('percentualPermuta', e.target.value)
                    }
                    className="input"
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="30">30%</option>
                    <option value="40">40%</option>
                    <option value="50">50%</option>
                    <option value="negociavel">Negociável</option>
                  </select>
                </div>
                <div>
                  <label className="label">Permuta negociável?</label>
                  <div className="flex gap-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="permutaNegociavel"
                        value="sim"
                        checked={form.permutaNegociavel === 'sim'}
                        onChange={(e) =>
                          handleChange('permutaNegociavel', e.target.value)
                        }
                      />
                      <span>Sim</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="permutaNegociavel"
                        value="nao"
                        checked={form.permutaNegociavel === 'nao'}
                        onChange={(e) =>
                          handleChange('permutaNegociavel', e.target.value)
                        }
                      />
                      <span>Não</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        <button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200"
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </form>

      <style jsx>{`
        .input {
          @apply block w-full p-3 border border-gray-300 rounded-lg text-base text-gray-900;
        }
        .label {
          @apply block text-sm font-medium text-gray-500 mb-2;
        }
      `}</style>
    </div>
  );
};

export default EditarImovel;
